/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { FloatingPixelBackground, PixelLogo } from '../components/pixel/PixelComponents';

const RECAPTCHA_SITE_KEY = '6LcqAVwsAAAAAEFyVDvBkvgTO2IufYJspOKS7A4A';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const RECAPTCHA_ENABLED = !IS_DEVELOPMENT;

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData({ name: '', email: '', phone: '', message: '' });
    setSubmitStatus(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      let recaptchaToken = '';
      if (RECAPTCHA_ENABLED && window.grecaptcha) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
        }
      }

      const response = await fetch('https://cloud.podstack.ai/auth/api/v1/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptcha_token: recaptchaToken }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else if (response.status === 422) {
        setSubmitStatus('disposable_email');
      } else if (response.status === 403) {
        setSubmitStatus('captcha_error');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-gray-100 relative">
      {/* FloatingPixelBackground self-clips via `fixed inset-0 overflow-hidden`; an
          overflow-hidden here would instead become the sticky header's clipping
          ancestor and break `position: sticky` in Safari. */}
      <FloatingPixelBackground intensity="low" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-primary-500/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PixelLogo scale={0.8} animate={false} />
              <span className="text-xl font-bold text-primary-500">Podstack</span>
            </div>
            <Link
              to="/login"
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-800 hover:border-primary-500/50 rounded-lg transition-all"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16 relative z-10">
        {/* Title */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Contact <span className="text-primary-500">Us</span>
          </h1>
          <p className="text-gray-400">
            We'd love to hear from you. Reach out to us through any of the channels below.
          </p>
        </div>

        {/* Table of Contents */}
        <nav className="mb-12 p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-4">Table of Contents</h2>
          <ol className="grid md:grid-cols-2 gap-2 text-sm text-gray-400">
            {[
              'Contact Information',
              'Email Contacts',
              'Business Hours',
              'Contact Form',
              'Grievance Officer',
              'Office Address',
            ].map((item, index) => (
              <li key={index}>
                <a href={`#section-${index + 1}`} className="hover:text-primary-400 transition-colors">
                  {index + 1}. {item}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Content Sections */}
        <div className="space-y-12">

          {/* Section 1: Contact Information */}
          <section id="section-1">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              1. Contact Information
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              Razordynamics Private Limited (operating as Podstack) is a GPU cloud computing platform providing on-demand access to high-performance GPUs for machine learning, AI training, and inference workloads.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              We are committed to providing excellent support to our customers. Whether you have a question about our GPU cloud services, need help with billing, or want to explore enterprise solutions, our team is here to help.
            </p>
            <div className="mt-4 p-6 bg-gray-900/50 rounded-xl border border-gray-800">
              <div className="space-y-2 text-sm">
                <p className="text-gray-300">
                  <strong className="text-white">Phone:</strong>{' '}
                  <a href="tel:+919620598447" className="text-primary-400 hover:underline">+91 96205 98447</a>
                  <span className="text-gray-500"> (Mon - Fri, 10:00 AM - 6:00 PM IST)</span>
                </p>
                <p className="text-gray-300">
                  <strong className="text-white">Email:</strong>{' '}
                  <a href="mailto:support@podstack.ai" className="text-primary-400 hover:underline">support@podstack.ai</a>
                </p>
                <p className="text-gray-300">
                  <strong className="text-white">Registered Address:</strong> 11th Floor, Prestige Tech Park, Platina 2, Outer Ring Rd, Kadubeesanahalli, Bengaluru, Karnataka India- 560087
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Email Contacts */}
          <section id="section-2">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              2. Email Contacts
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">General Inquiries</h4>
                <p className="text-gray-400 text-sm">
                  For general questions about Podstack and our services.
                </p>
                <a href="mailto:hello@podstack.ai" className="text-primary-400 hover:underline text-sm mt-2 inline-block">hello@podstack.ai</a>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">Technical Support</h4>
                <p className="text-gray-400 text-sm">
                  For help with platform issues, GPU instances, or technical queries.
                </p>
                <a href="mailto:support@podstack.ai" className="text-primary-400 hover:underline text-sm mt-2 inline-block">support@podstack.ai</a>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">Sales & Enterprise</h4>
                <p className="text-gray-400 text-sm">
                  For custom pricing, enterprise plans, and partnership opportunities.
                </p>
                <a href="mailto:sales@podstack.ai" className="text-primary-400 hover:underline text-sm mt-2 inline-block">sales@podstack.ai</a>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">Billing & Payments</h4>
                <p className="text-gray-400 text-sm">
                  For invoices, payment issues, refunds, and billing queries.
                </p>
                <a href="mailto:billing@podstack.ai" className="text-primary-400 hover:underline text-sm mt-2 inline-block">billing@podstack.ai</a>
              </div>
            </div>
          </section>

          {/* Section 3: Business Hours */}
          <section id="section-3">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              3. Business Hours
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              Our support team is available during the following hours:
            </p>
            <div className="mt-4 p-6 bg-gray-900/50 rounded-xl border border-gray-800">
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Monday - Friday</span>
                  <span className="text-white font-medium">10:00 AM - 6:00 PM IST</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Saturday</span>
                  <span className="text-white font-medium">10:00 AM - 2:00 PM IST</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Sunday & Public Holidays</span>
                  <span className="text-gray-500 font-medium">Closed</span>
                </div>
              </div>
            </div>
            <div className="bg-primary-500/10 border border-primary-500/30 rounded-xl p-4 mt-4">
              <p className="text-primary-300 text-sm">
                <strong>Note:</strong> For urgent infrastructure issues, our platform monitoring team is available 24/7. Critical alerts are handled outside business hours.
              </p>
            </div>
            <p className="text-gray-300 leading-relaxed mt-4">
              We aim to respond to all email inquiries within <strong>24 business hours</strong>. Enterprise customers with premium support plans receive priority response times.
            </p>
          </section>

          {/* Section 4: Contact Form */}
          <section id="section-4">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              4. Contact Form
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4 mb-6">
              Fill out the form below and our team will get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-gray-900/50 rounded-xl border border-gray-800">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full px-4 py-2.5 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-300 mb-1.5">
                  Email <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-2.5 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-2.5 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1.5">Message / Requirements</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project or requirements..."
                  rows={5}
                  className="w-full px-4 py-2.5 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none"
                />
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">Message Sent Successfully!</span>
                  </div>
                  <p className="text-green-300/80 text-sm ml-7">
                    Our team will review your message and get back to you within 24 hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Something went wrong. Please try again.
                </div>
              )}

              {submitStatus === 'disposable_email' && (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Please use a valid business or personal email. Temporary email services are not allowed.
                </div>
              )}

              {submitStatus === 'captcha_error' && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Security verification failed. Please refresh and try again.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg font-medium hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex-1 py-2.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </div>
            </form>
          </section>

          {/* Section 5: Grievance Officer */}
          <section id="section-5">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              5. Grievance Officer
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              In accordance with the Information Technology Act, 2000 and the rules made thereunder, the name and contact details of the Grievance Officer are provided below:
            </p>
            <div className="mt-4 p-6 bg-gray-900/50 rounded-xl border border-gray-800">
              <div className="space-y-2 text-sm">
                <p className="text-gray-300"><strong className="text-white">Name:</strong> Saurav Kumar</p>
                <p className="text-gray-300"><strong className="text-white">Designation:</strong> Director</p>
                <p className="text-gray-300"><strong className="text-white">Email:</strong> <a href="mailto:grievance@podstack.ai" className="text-primary-400 hover:underline">grievance@podstack.ai</a></p>
                <p className="text-gray-300"><strong className="text-white">Phone:</strong> <a href="tel:+919620598447" className="text-primary-400 hover:underline">+91 96205 98447</a></p>
                <p className="text-gray-300"><strong className="text-white">Address:</strong> 11th Floor, Prestige Tech Park, Platina 2, Outer Ring Rd, Kadubeesanahalli, Bengaluru, Karnataka India- 560087</p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed mt-4">
              The Grievance Officer shall acknowledge complaints within <strong>48 hours</strong> and resolve them within <strong>one month</strong> from the date of receipt of the grievance.
            </p>
          </section>

          {/* Section 6: Office Address */}
          <section id="section-6">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              6. Office Address
            </h2>
            <div className="mt-4 p-6 bg-gradient-to-r from-primary-500/10 to-transparent rounded-xl border border-primary-500/30">
              <p className="text-gray-300 text-sm">
                <strong className="text-white">Razordynamics Private Limited</strong> (operating as Podstack)<br />
                Address: 11th Floor, Prestige Tech Park, Platina 2,<br />
                Outer Ring Rd, Kadubeesanahalli, Bengaluru, Karnataka India- 560087
              </p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 px-6 relative z-10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <PixelLogo scale={0.6} animate={false} />
            <span className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} Podstack. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap gap-6 text-gray-500 text-sm">
            <Link to="/privacy-policy" className="hover:text-primary-400 transition-colors">Privacy Policy</Link>
            <a href="https://podstack.ai/terms" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors">Terms of Service</a>
            <span className="text-primary-400">Contact Us</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;
